/**
 * Zod schema contract for rm-api responses (Phase 2, T021).
 *
 * Two sources:
 *  1. Generated (schemas.gen.ts) — API types exported via schemars
 *     (W3 single source of truth): JobResponse, UserInfo, ScenePresignResponse.
 *  2. Hand-written below — response wrappers + leaf types that live in rm-api
 *     outside the exporter roots, so schemars can't reach them. Machine schemas are
 *     deferred to T040 (data-model §Machine, H2).
 *
 * Field-by-field verified against the backend on 2026-06-26 (source of truth in
 * comments). Corrections found vs data-model.md are noted inline (C1/C3).
 *
 * Policy: response schemas are tolerant (Zod's default object strips unknown
 * keys → backend can add fields without crashing the UI). Request bodies are
 * `.strict()` (catch client typos).
 */
import { z } from 'zod'
import {
  JobResponseSchema,
  UserInfoSchema,
  ScenePresignResponseSchema,
  JobAttemptsResponseSchema,
} from './schemas.gen'

export {
  JobResponseSchema,
  UserInfoSchema,
  ScenePresignResponseSchema,
  JobAttemptsResponseSchema,
}
export type JobAttemptsResponse = z.infer<typeof JobAttemptsResponseSchema>
export type JobAttempt = JobAttemptsResponse['attempts'][number]

export type UserInfo = z.infer<typeof UserInfoSchema>
// `JobSchema` / `Job` are defined below, after `RenderModeEnum`, so the typed
// `job_spec` union can reuse it.

// ── Enums (mirror backend, snake_case) ───────────────────────────────────

/** 11 variants — mirror `JobStatus`. Strict: unknown status = hard parse error. */
export const JobStatusEnum = z.enum([
  'preparing',
  'queued',
  'provisioning',
  'rendering',
  'uploading',
  'done',
  'delivering',
  'delivered',
  'delivery_failed',
  'failed',
  'cancelled',
])
export type JobStatus = z.infer<typeof JobStatusEnum>

/** Terminal statuses — stop polling (FR-009). Mirror `JobStatus::is_terminal`. */
export const TERMINAL_STATUSES = [
  'done',
  'delivered',
  'delivery_failed',
  'failed',
  'cancelled',
] as const satisfies readonly JobStatus[]

export const RenderModeEnum = z.enum([
  'animation',
  'tour',
  'turntable',
  'topview',
])
export type RenderMode = z.infer<typeof RenderModeEnum>

// ── job_spec (RM-116): typed discriminated union ─────────────────────────
// json-schema-to-zod renders the internally-tagged `JobSpec` as
// `z.any().superRefine(...)` (no clean discriminated union), so we hand-write a
// typed one here and override the generated `job_spec` field on JobSchema.
// Strict on `type` (an unknown type is a hard parse error — same philosophy as
// the strict status enum that drives polling). `.passthrough()` keeps unknown
// fields so backend additions don't drop data (response-tolerance policy).
const ModeParamRefSchema = z.object({ mode: RenderModeEnum }).passthrough()

export const BlenderRenderSpecSchema = z
  .object({
    type: z.literal('blender_render'),
    blender_version: z.string().optional(),
    gpu_type: z.string().optional(),
    mode_params: z.array(ModeParamRefSchema).default([]),
  })
  .passthrough()

export const EmptySpecSchema = z
  .object({
    type: z.literal('empty'),
    wait_secs: z.number().int(),
    exit_code: z.number().int().optional(),
  })
  .passthrough()

// RM-117: upscaling job type (TopazUpscaler worker). Values mirror
// job_spec::UpscalerKind (PascalCase, matching the worker env tokens).
export const UpscalerKindEnum = z.enum(['Topaz', 'Simple', 'ClearReality'])
export type UpscalerKind = z.infer<typeof UpscalerKindEnum>
/** Topaz model strength (mirror job_spec::TopazPower). */
export const TopazPowerEnum = z.enum(['Low', 'Medium', 'High'])
export type TopazPower = z.infer<typeof TopazPowerEnum>
/** chaiNNer resize interpolation (mirror job_spec::InterpolationMethod). */
export const InterpolationMethodEnum = z.enum([
  'auto',
  'nearest',
  'box',
  'linear',
  'cubic',
  'lanczos',
  'hermite',
  'mitchell',
  'b_spline',
  'hamming',
  'hann',
  'lagrange',
  'gauss',
])
export type InterpolationMethod = z.infer<typeof InterpolationMethodEnum>

export const UpscalingSpecSchema = z
  .object({
    type: z.literal('upscaling'),
    view_id: z.string(),
    // Name of the preset this job was built from, if any. Carried only so the
    // worker can tag the resulting render (custom jobs omit it).
    preset_name: z.string().optional(),
    // Which frame-elements set (`-f-N`) drives the investment overlay mask; the
    // worker resolves a missing/absent number to the view's production set, else
    // the highest available. Per-view, so it's on the job spec, not the preset.
    frame_elements_id: z.number().int().optional(),
    main_upscaler: UpscalerKindEnum,
    topaz_version: z.string().optional(),
    // Split Topaz controls (factor 2/4/6 · power Low/Medium/High).
    topaz_upscale_factor: z.number().int().optional(),
    topaz_power: TopazPowerEnum.optional(),
    max_brightness_adjustment: z.boolean().optional(),
    // Absent → native mode (target from the widest source; Topaz-only re-detail).
    target_size: z.number().int().optional(),
    investment_upscaler: UpscalerKindEnum.optional(),
    // Native mode only: use raw source frames for the building overlay.
    investment_use_source: z.boolean().optional(),
    investment_upscale_factor: z.number().int().optional(),
    investment_power: TopazPowerEnum.optional(),
    merge_feather_px: z.number().int().optional(),
    // Per-branch chaiNNer resize interpolation (experimental).
    main_simple_interpolation: InterpolationMethodEnum.optional(),
    main_downscale_interpolation: InterpolationMethodEnum.optional(),
    main_resize_interpolation: InterpolationMethodEnum.optional(),
    investment_simple_interpolation: InterpolationMethodEnum.optional(),
    investment_downscale_interpolation: InterpolationMethodEnum.optional(),
    investment_resize_interpolation: InterpolationMethodEnum.optional(),
  })
  .passthrough()

const TurntableManifestSchema = z
  .object({
    schema_version: z.literal(1),
    near_clip: z.number(),
    far_clip: z.number(),
    orbit_center: z.array(z.number()).length(3),
    frames: z.array(z.unknown()).length(120),
  })
  .passthrough()

export const TurntableFrameBakeSpecSchema = z
  .object({
    type: z.literal('turntable_frame_bake'),
    color_view_id: z.string(),
    depth_view_id: z.string(),
    output_target_id: z.string(),
    output_frame_count: z.number().int().min(1).max(2400),
    manifest: TurntableManifestSchema,
  })
  .passthrough()

// ── Upscaling presets (global, shared) ─────────────────────────────────────
// A preset stores the upscaling config only — the form fields minus the per-view
// view_id. Mirrors rm-api `UpscalingPresetSpec`.
export const UpscalingPresetSpecSchema = z
  .object({
    main_upscaler: UpscalerKindEnum,
    topaz_version: z.string().optional(),
    topaz_upscale_factor: z.number().int().optional(),
    topaz_power: TopazPowerEnum.optional(),
    max_brightness_adjustment: z.boolean().optional(),
    // Absent → native mode (target from the widest source; Topaz-only re-detail).
    target_size: z.number().int().optional(),
    investment_upscaler: UpscalerKindEnum.optional(),
    // Native mode only: use raw source frames for the building overlay.
    investment_use_source: z.boolean().optional(),
    investment_upscale_factor: z.number().int().optional(),
    investment_power: TopazPowerEnum.optional(),
    merge_feather_px: z.number().int().optional(),
    main_simple_interpolation: InterpolationMethodEnum.optional(),
    main_downscale_interpolation: InterpolationMethodEnum.optional(),
    main_resize_interpolation: InterpolationMethodEnum.optional(),
    investment_simple_interpolation: InterpolationMethodEnum.optional(),
    investment_downscale_interpolation: InterpolationMethodEnum.optional(),
    investment_resize_interpolation: InterpolationMethodEnum.optional(),
  })
  .passthrough()
export type UpscalingPresetSpec = z.infer<typeof UpscalingPresetSpecSchema>

export const UpscalingPresetSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    spec: UpscalingPresetSpecSchema,
    // Whether the current user may update/delete this preset (creator or admin).
    can_edit: z.boolean(),
    // Listed to external apps. The creator or a system admin can change this.
    is_public: z.boolean(),
    created_at: z.string(),
    updated_at: z.string(),
  })
  .passthrough()
export type UpscalingPreset = z.infer<typeof UpscalingPresetSchema>

export const UpscalingPresetsResponseSchema = z.object({
  presets: z.array(UpscalingPresetSchema),
})
export type UpscalingPresetsResponse = z.infer<
  typeof UpscalingPresetsResponseSchema
>

export const SurroundingsSpecSchema = z
  .object({
    type: z.literal('surroundings'),
    run_id: z.string().uuid(),
    request_sha256: z.string().regex(/^[0-9a-f]{64}$/),
    worker_release: z.string().regex(/^[0-9a-f]{64}$/),
    compute: z.enum(['cpu', 'gpu']),
  })
  .passthrough()

export const JobSpecSchema = z.discriminatedUnion('type', [
  BlenderRenderSpecSchema,
  EmptySpecSchema,
  UpscalingSpecSchema,
  SurroundingsSpecSchema,
  TurntableFrameBakeSpecSchema,
])
export type JobSpec = z.infer<typeof JobSpecSchema>

type JsonRecord = Record<string, unknown>

function isJsonRecord(value: unknown): value is JsonRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * json-schema-to-zod currently turns nullable enum fields into optional-only
 * fields. The workflow reducer serializes absent projection enums as `null`, so
 * normalize that wire representation to omission before generated validation.
 */
function normalizeWorkflowProjectionEnums(progress: unknown): unknown {
  if (!isJsonRecord(progress) || progress.v !== 2) return progress

  const projections = progress.projections
  if (!isJsonRecord(projections)) return progress

  let changed = false
  const normalizedProjections = Object.fromEntries(
    Object.entries(projections).map(([id, projection]) => {
      if (!isJsonRecord(projection)) return [id, projection]

      const normalized = { ...projection }
      for (const field of ['unit', 'accuracy'] as const) {
        if (normalized[field] === null) {
          delete normalized[field]
          changed = true
        }
      }
      return [id, normalized]
    }),
  )

  return changed
    ? { ...progress, projections: normalizedProjections }
    : progress
}

const JobProgressSchema = z.preprocess(
  normalizeWorkflowProjectionEnums,
  JobResponseSchema.shape.progress,
)

// Generation gaps patched here:
//  - setup_stage: json-schema-to-zod drops the `null` member when a field is
//    both `enum` and nullable, so `Option<SetupStage>` came out non-nullable —
//    the backend DOES send null. (delivery_config / mode_results were correct.)
//  - progress: nullable projection enums need wire normalization before the
//    generated schema validates them.
//  - job_spec: replace the generated `z.any()` with the typed union above.
export const JobSchema = JobResponseSchema.extend({
  setup_stage: JobResponseSchema.shape.setup_stage.nullable(),
  progress: JobProgressSchema,
  job_spec: JobSpecSchema,
})
export type Job = z.infer<typeof JobSchema>

// ── User / auth ──────────────────────────────────────────────────────────

// ⚠️ C1 correction: `/me` (UserProfileResponse) uses `#[serde(flatten)]` on the
// user — the response is FLATTENED (UserInfo fields + beta_approved at top
// level), NOT `{ user, beta_approved }` as data-model.md §1 states. Verified:
// crates/rm-api/src/auth/types.rs:236.
/** Active organization for the session, mirrored from the JWT `o` claim.
 * `null` in personal context. From `/me` (crates/rm-api/src/auth/types.rs). */
export const ActiveOrgSchema = z
  .object({
    id: z.string(),
    slug: z.string(),
    role: z.string(),
  })
  .passthrough()
export type ActiveOrg = z.infer<typeof ActiveOrgSchema>

export const UserProfileResponseSchema = UserInfoSchema.extend({
  beta_approved: z.boolean(),
  is_system_admin: z.boolean(),
  is_product_analytics_reader: z.boolean().default(false),
  // Dedicated tenant capability; independent from platform administration.
  can_create_organization: z.boolean().default(false),
  // Nullish (not just nullable) for rollback tolerance — older backends omit it.
  active_org: ActiveOrgSchema.nullish(),
  // May this account use the personal (non-org) context. Org-only signups are
  // `false`. Defaults to `true` for rollback tolerance (older backends omit it).
  personal_scope: z.boolean().default(true),
  // Organization products (e.g. `plotea`) of the active organization. Empty in
  // personal context; defaults to `[]` for older backends.
  products: z.array(z.string()).default([]),
}).passthrough()
export type UserProfileResponse = z.infer<typeof UserProfileResponseSchema>

// ── Organizations (Neon Auth org-plugin proxy; camelCase like the payloads) ──

export const OrganizationSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    slug: z.string(),
    logo: z.string().nullish(),
    createdAt: z.string().nullish(),
    metadata: z.unknown().nullish(),
    // Present on `GET /api/v1/organizations` items: products the organization
    // may use right now (e.g. `plotea`).
    products: z.array(z.string()).default([]),
  })
  .passthrough()
export type Organization = z.infer<typeof OrganizationSchema>

export const OrgMemberUserSchema = z
  .object({
    id: z.string(),
    name: z.string().nullish(),
    email: z.string(),
    image: z.string().nullish(),
  })
  .passthrough()

export const OrgMemberSchema = z
  .object({
    id: z.string(),
    organizationId: z.string(),
    userId: z.string(),
    role: z.string(),
    createdAt: z.string().nullish(),
    user: OrgMemberUserSchema.nullish(),
  })
  .passthrough()
export type OrgMember = z.infer<typeof OrgMemberSchema>

export const OrgInvitationSchema = z
  .object({
    id: z.string(),
    organizationId: z.string(),
    email: z.string(),
    role: z.string(),
    status: z.string(),
    expiresAt: z.string().nullish(),
    inviterId: z.string().nullish(),
    // Display enrichment for the invitee (may be absent depending on provider).
    organizationName: z.string().nullish(),
    inviterName: z.string().nullish(),
    inviterEmail: z.string().nullish(),
  })
  .passthrough()
export type OrgInvitation = z.infer<typeof OrgInvitationSchema>

export const FullOrganizationSchema = OrganizationSchema.extend({
  members: z.array(OrgMemberSchema).default([]),
  invitations: z.array(OrgInvitationSchema).default([]),
}).passthrough()
export type FullOrganization = z.infer<typeof FullOrganizationSchema>

export const OrganizationsResponseSchema = z.array(OrganizationSchema)
export const OrgMembersResponseSchema = z.array(OrgMemberSchema)
export const OrgInvitationsResponseSchema = z.array(OrgInvitationSchema)

export const PluginProductSchema = z.enum(['archigma_rhino'])
export type PluginProduct = z.infer<typeof PluginProductSchema>

export const MembershipPluginAccessSchema = z.object({
  organization_id: z.string().uuid(),
  membership_id: z.string().uuid(),
  account_id: z.string().uuid(),
  product: PluginProductSchema,
  granted: z.boolean(),
  revision: z.number().int().nonnegative(),
  updated_at: z.string().datetime({ offset: true }).nullable(),
})
export type MembershipPluginAccess = z.infer<
  typeof MembershipPluginAccessSchema
>

export const PluginAccessMemberSchema = z.object({
  membership_id: z.string().uuid(),
  account_id: z.string().uuid(),
  email: z.string().nullable(),
  name: z.string().nullable(),
  role: z.string(),
  access: z.array(MembershipPluginAccessSchema),
})

export const PluginAccessEventSchema = z.object({
  id: z.string().uuid(),
  organization_id: z.string().uuid(),
  membership_id: z.string().uuid(),
  account_id: z.string().uuid(),
  actor_account_id: z.string().uuid().nullable(),
  actor_kind: z.enum(['administrator', 'system']),
  product: PluginProductSchema,
  action: z.enum(['grant', 'revoke', 'migration_grant']),
  granted: z.boolean(),
  revision: z.number().int().nonnegative(),
  created_at: z.string().datetime({ offset: true }),
})

export const PluginAccessOverviewSchema = z.object({
  organization_id: z.string().uuid(),
  products: z.array(
    z.object({ product: PluginProductSchema, available: z.boolean() }),
  ),
  members: z.array(PluginAccessMemberSchema),
  history: z.array(PluginAccessEventSchema),
})
export type PluginAccessOverview = z.infer<typeof PluginAccessOverviewSchema>

export const SetPluginAccessResponseSchema = z.object({
  outcome: z.enum(['applied', 'already_achieved', 'replayed']),
  access: MembershipPluginAccessSchema,
})
export type SetPluginAccessResponse = z.infer<
  typeof SetPluginAccessResponseSchema
>

export const LoreOrganizationRoleSchema = z.enum(['member', 'admin', 'owner'])
export const LoreOrganizationOperationStatusSchema = z.enum([
  'pending',
  'processing',
  'succeeded',
  'failed',
  'expired',
  'needs_review',
])
export const LoreOrganizationOperationActionSchema = z.discriminatedUnion(
  'kind',
  [
    z.object({
      kind: z.literal('invite_member'),
      email: z.string(),
      role: LoreOrganizationRoleSchema,
    }),
    z.object({
      kind: z.literal('remove_member'),
      userId: z.string().uuid(),
      email: z.string(),
    }),
  ],
)
export const LoreOrganizationOperationSchema = z.object({
  operationId: z.string().uuid(),
  action: LoreOrganizationOperationActionSchema,
  status: LoreOrganizationOperationStatusSchema,
  failureCode: z
    .enum(['provider_rejected', 'provider_outcome_unknown'])
    .nullable(),
  expiresAt: z.string().datetime({ offset: true }),
})

export const DesktopAuthorizeResponseSchema = z
  .object({
    callback_url: z.string().url().startsWith('http://'),
  })
  .strict()

export const DesktopLoginPreviewResponseSchema = z
  .object({
    product: z.enum([
      'rendermoon_blender',
      'archigma_rhino',
      'archigma_datasmith',
      'archigma_unreal',
    ]),
    scopes: z.array(
      z.enum([
        'account_read',
        'render_submit',
        'asset_catalog_read',
        'asset_catalog_write',
        'analytics_policy_read',
        'analytics_submit',
        'release_download',
        'geometry_export',
      ]),
    ),
  })
  .strict()
export type LoreOrganizationOperation = z.infer<
  typeof LoreOrganizationOperationSchema
>

// Admin user list (GET /admin/users). Hand-written — the row type lives in
// rm-api/handlers/admin.rs (Neon Auth `neon_auth."user"` mirror + joins), out of
// schemars scope. Tolerant parse: passthrough + nullable optionals.
export const AdminUserSchema = z
  .object({
    id: z.string(),
    email: z.string(),
    name: z.string().nullable(),
    email_verified: z.boolean(),
    is_system_admin: z.boolean(),
    beta_approved: z.boolean(),
    can_create_organization: z.boolean().default(false),
    // Personal-scope entitlement (org-only accounts are `false`).
    personal_scope: z.boolean().default(true),
    created_at: z.string().nullable(),
  })
  .passthrough()
export type AdminUser = z.infer<typeof AdminUserSchema>

export const AdminUsersResponseSchema = z
  .object({ users: z.array(AdminUserSchema) })
  .passthrough()
export type AdminUsersResponse = z.infer<typeof AdminUsersResponseSchema>

/** POST /auth/login → `{ user: UserInfo }`. */
export const LoginResponseSchema = z
  .object({ user: UserInfoSchema })
  .passthrough()

// GET /dev/seed-users → dev-only quick-login accounts (empty on prod). Source:
// crates/rm-api/src/dev_seed.rs.
export const SeedUserSchema = z
  .object({
    email: z.string(),
    password: z.string(),
    label: z.string(),
  })
  .passthrough()
export type SeedUser = z.infer<typeof SeedUserSchema>

export const SeedUsersResponseSchema = z
  .object({ users: z.array(SeedUserSchema) })
  .passthrough()

// ── Jobs ─────────────────────────────────────────────────────────────────

// GET /jobs → ListJobsResponse. Source: crates/rm-api/src/handlers/mod.rs:92.
export const JobsResponseSchema = z
  .object({
    jobs: z.array(JobSchema),
    next_cursor: z.string().nullish(),
  })
  .passthrough()
export type JobsResponse = z.infer<typeof JobsResponseSchema>

// submit/cancel/confirm → JobMutationResponse. Source: crates/rm-api/src/api_types.rs.
export const JobMutationResponseSchema = z
  .object({
    data: JobSchema,
  })
  .passthrough()
export type JobMutationResponse = z.infer<typeof JobMutationResponseSchema>

// GET /jobs/{id}/logs → JobLogsResponse. Source: handlers/jobs.rs:357 (JobLogRow:364).
export const JobLogSchema = z
  .object({
    id: z.string(),
    seq: z.number().int(),
    job_id: z.string().uuid(),
    ts: z.string(),
    line: z.string(),
  })
  .passthrough()
export type JobLog = z.infer<typeof JobLogSchema>

export const JobLogsResponseSchema = z
  .object({
    job_id: z.string().uuid(),
    count: z.number().int(),
    lines: z.array(JobLogSchema),
  })
  .passthrough()
export type JobLogsResponse = z.infer<typeof JobLogsResponseSchema>

// ── Frames ───────────────────────────────────────────────────────────────

// ⚠️ C3 correction: GET /jobs/{id}/frames returns FrameResponse (presigned
// download/thumbnail URLs), NOT the raw render_images row {id, r2_path} that
// data-model.md §Frame lists. Source: crates/rm-api/src/handlers/mod.rs:189.
export const FrameSchema = z
  .object({
    image_index: z.number().int(),
    format: z.string(),
    size_bytes: z.number().int(),
    download_url: z.string(),
    thumbnail_url: z.string().nullish(),
    expires_in_seconds: z.number().int(),
    created_at: z.string(),
  })
  .passthrough()
export type Frame = z.infer<typeof FrameSchema>

// GET /jobs/{id}/frames → ListFramesResponse. Source: handlers/mod.rs:199.
export const FramesResponseSchema = z
  .object({
    frames: z.array(FrameSchema),
    next_cursor: z.string().nullish(),
    total_frames: z.number().int(),
    expires_at: z.string().nullish(),
  })
  .passthrough()
export type FramesResponse = z.infer<typeof FramesResponseSchema>

// ── Asset Hub (organization-scoped LORE index) ─────────────────────────

export const AssetVersionStateEnum = z.enum([
  'candidate',
  'approved',
  'rejected',
  'deprecated',
])
export type AssetVersionState = z.infer<typeof AssetVersionStateEnum>

export const StyleVersionStateEnum = z.enum([
  'draft',
  'published',
  'deprecated',
])

export const LoreMediaRefSchema = z
  .object({
    id: z.string().uuid(),
    content_url: z.string(),
    repository_id: z.string(),
    lore_path: z.string(),
    content_digest: z.string(),
    mime_type: z.string(),
    width_px: z.number().int().positive().nullish(),
    height_px: z.number().int().positive().nullish(),
  })
  .passthrough()

export const AssetSummarySchema = z
  .object({
    id: z.string().uuid(),
    stable_id: z.string(),
    display_name: z.string(),
    category: z.string(),
    current_version_id: z.string().uuid().nullish(),
    version_label: z.string().nullish(),
    state: AssetVersionStateEnum.nullish(),
    compatible_kind: z.string().nullish(),
    document_kind: z.string().nullish(),
    schema_name: z.string().nullish(),
    schema_version: z.number().int().nullish(),
    thumbnail: LoreMediaRefSchema.nullish(),
    style_count: z.number().int().nonnegative(),
    open_task_count: z.number().int().nonnegative(),
  })
  .passthrough()
export type AssetSummary = z.infer<typeof AssetSummarySchema>

export const AssetsResponseSchema = z
  .object({
    assets: z.array(AssetSummarySchema),
    total: z.number().int().nonnegative(),
  })
  .passthrough()

export const StyleSummarySchema = z
  .object({
    id: z.string().uuid(),
    stable_id: z.string(),
    display_name: z.string(),
    purpose: z.string().nullish(),
    current_version_id: z.string().uuid().nullish(),
    version_label: z.string().nullish(),
    state: StyleVersionStateEnum.nullish(),
    asset_count: z.number().int().nonnegative(),
    missing_asset_count: z.number().int().nonnegative(),
    thumbnail: LoreMediaRefSchema.nullish(),
  })
  .passthrough()
export type StyleSummary = z.infer<typeof StyleSummarySchema>

export const StylesResponseSchema = z
  .object({ styles: z.array(StyleSummarySchema) })
  .passthrough()

export const StudioMediaSchema = z
  .object({ role: z.string(), media: LoreMediaRefSchema })
  .passthrough()

export const StudioRunSchema = z
  .object({
    id: z.string().uuid(),
    repository_id: z.string(),
    lore_path: z.string(),
    studio_profile_id: z.string(),
    generator_version: z.string(),
    validation_state: z.string(),
    created_at: z.string(),
    media: z.array(StudioMediaSchema),
  })
  .passthrough()

export const AssetVersionSchema = z
  .object({
    id: z.string().uuid(),
    content_url: z.string().nullable(),
    repository_id: z.string(),
    lore_path: z.string(),
    lore_fragment: z.string().nullish(),
    content_digest: z.string(),
    version_label: z.string(),
    state: AssetVersionStateEnum,
    document_kind: z.string(),
    schema_name: z.string().nullish(),
    schema_version: z.number().int().nullish(),
    compatible_kind: z.string().nullish(),
    artifact_kind: z.string().nullish(),
    config: z.record(z.string(), z.unknown()),
    source: z.record(z.string(), z.unknown()),
    bounds_m: z.unknown().nullish(),
    pivot_kind: z.string().nullish(),
    pivot_m: z.unknown().nullish(),
    front_direction: z.unknown().nullish(),
    up_direction: z.unknown().nullish(),
    indexed_at: z.string(),
    studio: z.array(StudioRunSchema),
  })
  .passthrough()
export type AssetVersion = z.infer<typeof AssetVersionSchema>

export const AssetStyleUsageSchema = z
  .object({
    style_id: z.string(),
    style_display_name: z.string(),
    style_version_id: z.string().uuid(),
    style_version_label: z.string(),
    room_type: z.string().nullish(),
    furniture_kind: z.string(),
    member_role: z.string(),
    config_id: z.string().nullish(),
  })
  .passthrough()

export const AssetDetailResponseSchema = z
  .object({
    id: z.string().uuid(),
    stable_id: z.string(),
    display_name: z.string(),
    category: z.string(),
    current_approved_version_id: z.string().uuid().nullish(),
    versions: z.array(AssetVersionSchema),
    style_usages: z.array(AssetStyleUsageSchema),
  })
  .passthrough()
export type AssetDetail = z.infer<typeof AssetDetailResponseSchema>

export const AssetTaskPriorityEnum = z.enum(['low', 'normal', 'high', 'urgent'])
export type AssetTaskPriority = z.infer<typeof AssetTaskPriorityEnum>

export const AssetTaskStatusEnum = z.enum([
  'open',
  'in_progress',
  'ready_for_review',
  'changes_requested',
  'accepted',
  'cancelled',
])
export type AssetTaskStatus = z.infer<typeof AssetTaskStatusEnum>

export const AssetTaskCommentSchema = z
  .object({
    id: z.string().uuid(),
    author_kind: z.string(),
    author_id: z.string(),
    author_display_name: z.string(),
    body: z.string(),
    created_at: z.string(),
  })
  .passthrough()

export const AssetTaskEvidenceSchema = z
  .object({
    id: z.string().uuid(),
    content_url: z.string(),
    repository_id: z.string(),
    role: z.string(),
    lore_path: z.string(),
    content_digest: z.string(),
    mime_type: z.string(),
    width_px: z.number().int().positive().nullish(),
    height_px: z.number().int().positive().nullish(),
    submission_id: z.string().uuid().nullish(),
    created_at: z.string(),
  })
  .passthrough()

export const AssetTaskReviewSchema = z
  .object({
    decision: z.enum(['accept', 'changes_requested', 'reject']),
    note: z.string().nullish(),
    reviewer_id: z.string().uuid(),
    created_at: z.string(),
  })
  .passthrough()

export const AssetTaskSubmissionSchema = z
  .object({
    id: z.string().uuid(),
    base_asset_version_id: z.string().uuid(),
    candidate_asset_version_id: z.string().uuid(),
    candidate_content_digest: z.string(),
    submitted_by_agent_key_id: z.string().uuid(),
    result_summary: z.string(),
    source_revision: z.string().nullish(),
    validation_results: z.record(z.string(), z.unknown()),
    acceptance_response: z.record(z.string(), z.unknown()),
    created_at: z.string(),
    review: AssetTaskReviewSchema.nullish(),
  })
  .passthrough()

export const AssetTaskEventSchema = z
  .object({
    id: z.string().uuid(),
    event_type: z.string(),
    actor_kind: z.string(),
    actor_id: z.string(),
    from_status: AssetTaskStatusEnum.nullish(),
    to_status: AssetTaskStatusEnum.nullish(),
    payload: z.record(z.string(), z.unknown()),
    created_at: z.string(),
  })
  .passthrough()

export const AssetTaskSchema = z
  .object({
    id: z.string().uuid(),
    asset_stable_id: z.string(),
    asset_display_name: z.string(),
    asset_version_id: z.string().uuid(),
    asset_version_label: z.string(),
    output_asset_version_id: z.string().uuid().nullish(),
    title: z.string(),
    description: z.string(),
    priority: AssetTaskPriorityEnum,
    status: AssetTaskStatusEnum,
    required_capabilities: z.array(z.string()),
    created_by: z.string().uuid(),
    assignee_kind: z.string().nullish(),
    assignee_id: z.string().nullish(),
    result_summary: z.string().nullish(),
    row_version: z.number().int().positive(),
    created_at: z.string(),
    updated_at: z.string(),
    claimed_at: z.string().nullish(),
    submitted_at: z.string().nullish(),
    reviewed_at: z.string().nullish(),
    reviewed_by: z.string().uuid().nullish(),
    lease_expires_at: z.string().nullish(),
    last_heartbeat_at: z.string().nullish(),
    comments: z.array(AssetTaskCommentSchema),
    evidence: z.array(AssetTaskEvidenceSchema),
    submissions: z.array(AssetTaskSubmissionSchema),
    events: z.array(AssetTaskEventSchema),
  })
  .passthrough()
export type AssetTask = z.infer<typeof AssetTaskSchema>

export const AssetTasksResponseSchema = z
  .object({ tasks: z.array(AssetTaskSchema) })
  .passthrough()

// ── API keys ─────────────────────────────────────────────────────────────

// ApiKeyResponse. Source: crates/rm-api/src/auth/api_key.rs:27.
export const ApiKeySchema = z
  .object({
    id: z.string().uuid(),
    name: z.string(),
    key_prefix: z.string(),
    last_used_at: z.string().nullish(),
    created_at: z.string(),
    is_active: z.boolean(),
  })
  .passthrough()
export type ApiKey = z.infer<typeof ApiKeySchema>

// GET /api-keys → ListApiKeysResponse. Source: handlers/api_keys.rs:18.
export const ApiKeysResponseSchema = z
  .object({ keys: z.array(ApiKeySchema) })
  .passthrough()
export type ApiKeysResponse = z.infer<typeof ApiKeysResponseSchema>

// POST /api-keys → CreateApiKeyResponse (plaintext key shown once; no is_active).
// Source: crates/rm-api/src/auth/api_key.rs:51.
export const CreateApiKeyResponseSchema = z
  .object({
    id: z.string().uuid(),
    name: z.string(),
    key_prefix: z.string(),
    key: z.string(),
    created_at: z.string(),
  })
  .passthrough()
export type CreateApiKeyResponse = z.infer<typeof CreateApiKeyResponseSchema>

// ── Request bodies (strict) ──────────────────────────────────────────────

export const LoginRequestSchema = z
  .object({ email: z.string(), password: z.string() })
  .strict()

export const RegisterRequestSchema = z
  .object({ email: z.string(), password: z.string(), name: z.string() })
  .strict()

export const VerifyEmailRequestSchema = z
  .object({ email: z.string(), code: z.string() })
  .strict()

export const ResendVerificationRequestSchema = z
  .object({ email: z.string() })
  .strict()

export const CreateApiKeyRequestSchema = z.object({ name: z.string() }).strict()

/** Empty-body responses (register/verify/resend/logout/refresh/revoke). */
export const EmptyResponseSchema = z.unknown()
